import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Chip, 
  CircularProgress,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import DateFormatter from '../../../../../components/utils/DateFormatter';
import NameAvatar from '../../../../../components/avatars/Avatar';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import LanguageIcon from '@mui/icons-material/Language';

import { 
  GetSmartLCsBIZcore
} from '../../../../../redux/actions/business/core/smartLCsActions';

import SmartLCsApprovalsModal from './SmartLCsApprovalsModal';


const SmartLCsApprovalsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const [loading, setLoading] = useState(true);
  const { handleShowAmount } = useCurrencyService();
  // eslint-disable-next-line no-unused-vars
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [smartLCsModalOpen, setSmartLCsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [smartLCsList, setSmartLCsList] = useState([]);
  const [smartLCsTotal, setSmartLCsTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();
  
  const tableHeadingsList = [
    {
      label: "Company",
      sort: null,
    },
    {
      label: "Reference",
      sort: "entity_reference",
    },
    {
      label: "Vendor",
      sort: null,
    },
    {
      label: "Status",
      sort: "status",
    },
    {
      label: "Amount",
      sort: "total_base_amount",
      align: "right"
    },
    {
      label: "Expiry",
      sort: "expiry_date",
    },
    {
      label: "",
      sort: null,
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetSmartLCs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatSmartLCsList(state.smartLCsList.records || []);
      setSmartLCsTotal(state.smartLCsList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatSmartLCsList = (list) => {
    setSmartLCsList(list);
  };

  async function handleGetSmartLCs(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    await dispatch(GetSmartLCsBIZcore(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleGetSmartLCsDetails = (obj) => {
    setModalTitle(`Details for LC for - ${obj?.vendor?.name || ""}`);
    setUpdate({
      ...obj,
      defaultMode: 1,
    });
    setSmartLCsModalOpen(!smartLCsModalOpen);
  }; 
    
  return (
    <Box>

      <SmartLCsApprovalsModal 
        open={smartLCsModalOpen}
        setOpen={setSmartLCsModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetSmartLCs}
        />


      <TopBar
        title={"Smart LCs Approvals"}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          smartLCsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No Smart LC found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex} 
                      align={heading.align || "left"}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}
                      align={heading.align || "left"}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                smartLCsList.map((row, index) => (
                  <TableRow 
                    key={index}
                    className='table__clickable-row'
                    onClick={() => {handleGetSmartLCsDetails(row)}}>
                    <TableCell>
                      <Box
                        className="flexCenter_Row"
                        gap={"10px"}>
                        <NameAvatar
                          color={row.company?.color || null}>
                          {row.company?.company_name || "NA"}
                        </NameAvatar>
                        <Typography>
                          {row.company?.company_name || "NA"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {row.entity_reference || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {
                        row.vendor
                        ?
                        <Box
                          className="flexCenter_Row"
                          gap={"10px"}>
                          <NameAvatar
                            color={row.vendor?.color || null}>
                            {row.vendor?.name || "NA"}
                          </NameAvatar>
                          <Typography>
                            {row.vendor?.name || "NA"}
                          </Typography>
                          {
                            row.vendor?.is_international === true
                            &&
                            <Tooltip
                              title={"International vendor"}>
                              <LanguageIcon 
                                sx={{ color: "lightgray" }}
                                fontSize='small'/>
                            </Tooltip>
                          }
                          {
                            row?.vendor?.is_international === true
                            &&
                            <Chip 
                              size='small'
                              disabled
                              label={row?.vendor?.associated_international_vendor[0].currency}
                              />
                          }
                        </Box>
                        :
                        <NA 
                          label={"No supplier found"} />
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status || "NA"}
                        color={row.status === "ACTIVE_LOCKED" ? "success" : "default"}
                        variant={row.status === "ACTIVE_LOCKED" ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        {handleShowAmount(row.base_currency, row.total_base_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.expiry_date ? DateFormatter(row.expiry_date, "UTC") : <NA />}
                    </TableCell>
                    <TableCell>
                    
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={smartLCsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default SmartLCsApprovalsPage